import { useState, useEffect } from "react";
import { Eyebrow, Badge } from "@/components/ui";

interface ReviewCountdownProps {
  deadlineTimestamp: number;
  onDeadlineExpired?: () => void;
}

/**
 * ReviewCountdown — paper banner showing days/hours remaining
 * before the review window auto-advances. Quiet by default;
 * walnut tint at ≤3 days; flag tint at expired.
 */
export function ReviewCountdown({ deadlineTimestamp, onDeadlineExpired }: ReviewCountdownProps) {
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    const interval = setInterval(() => setNow(Date.now()), 60_000);
    return () => clearInterval(interval);
  }, []);

  const remaining = deadlineTimestamp - now;
  const expired = remaining <= 0;

  useEffect(() => { if (expired) onDeadlineExpired?.(); }, [expired, onDeadlineExpired]);

  if (expired) {
    return (
      <div className="rounded-as-3 bg-flag-tint border border-flag p-4">
        <div className="font-display font-semibold text-t-18 text-flag">Proof window has closed.</div>
        <div className="mt-1 font-text text-t-14 text-ink-2">
          PullPress moves forward with the current manuscript proof.
        </div>
      </div>
    );
  }

  const days = Math.floor(remaining / (1000 * 60 * 60 * 24));
  const hours = Math.floor((remaining % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));

  const isUrgent = days <= 3;
  const tone = isUrgent ? "bg-walnut-tint border border-walnut" : "bg-paper-raised border border-rule";

  return (
    <div className={`rounded-as-3 p-4 ${tone}`}>
      <div className="flex items-center justify-between gap-4">
        <div>
          <Eyebrow>Proof window</Eyebrow>
          <p className="font-display font-semibold text-t-21 leading-as-snug text-ink-1 mt-[6px]">
            {days} day{days !== 1 ? "s" : ""}{hours > 0 ? `, ${hours}h` : ""} to approve the book.
          </p>
          <p className="font-mono text-[11px] tracking-as-eyebrow uppercase text-ink-3 mt-[4px]">
            Closes {new Date(deadlineTimestamp).toLocaleDateString()}
          </p>
        </div>
        {isUrgent && (
          <Badge tone="contested">{days <= 1 ? "Final day" : "Closing soon"}</Badge>
        )}
      </div>
    </div>
  );
}
